import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { Layers, CheckCircle2, ClipboardList, Briefcase, Users, FileText, Check, Plus, Trash2, Calendar, ShieldAlert, Sparkles, Send } from 'lucide-react';
import { WorkSuiteTask, NexusUser } from '../types/nexus';
import { NexoraXSortDropdown } from './NexoraXSortDropdown';

interface NexusWorkSuiteProps {
  user: NexusUser;
  onAddLog: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
  tasks: WorkSuiteTask[];
  onTasksChanged: (newTasks: WorkSuiteTask[]) => void;
}

export const NexusWorkSuite: React.FC<NexusWorkSuiteProps> = ({
  user,
  onAddLog,
  tasks,
  onTasksChanged
}) => {
  const [activeTab, setActiveTab] = useState<'Tasks' | 'Projects' | 'Clients' | 'Approvals'>('Tasks');
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskProject, setNewTaskProject] = useState('Campaign Sprint');
  const [newTaskPriority, setNewTaskPriority] = useState<'URGENT' | 'HIGH' | 'MEDIUM' | 'LOW'>('HIGH');
  const [sortBy, setSortBy] = useState<'priority' | 'name' | 'lastModified'>('priority');
  const [isOverColumn, setIsOverColumn] = useState<Record<string, boolean>>({});

  const priorityMapTask: Record<string, number> = {
    'URGENT': 4,
    'HIGH': 3,
    'MEDIUM': 2,
    'LOW': 1
  };

  const sortedTasks = [...tasks].sort((a, b) => {
    if (sortBy === 'name') {
      return a.title.localeCompare(b.title);
    } else if (sortBy === 'lastModified') {
      const aTime = a.id.startsWith('task-') ? parseInt(a.id.split('-')[1]) || 0 : 0;
      const bTime = b.id.startsWith('task-') ? parseInt(b.id.split('-')[1]) || 0 : 0;
      return bTime - aTime;
    } else if (sortBy === 'priority') {
      const aPri = priorityMapTask[a.priority] || 2;
      const bPri = priorityMapTask[b.priority] || 2;
      if (aPri !== bPri) {
        return bPri - aPri;
      }
      return a.title.localeCompare(b.title);
    }
    return 0;
  });

  // Multi-module static structures for display
  const [projects, setProjects] = useState([
    { id: 'p-1', name: 'Nexus OS Landing Optimization', client: 'Director Office Direct', pct: 85, status: 'Active' },
    { id: 'p-2', name: 'NexoraX billing records Integration', client: 'Executive Support Division', pct: 60, status: 'Reviewing' },
    { id: 'p-3', name: 'Resource resource Distribution Pipeline', client: 'Global Charities Fund', pct: 30, status: 'Planning' }
  ]);

  const [clients, setClients] = useState([
    { id: 'c-1', name: 'Executive Support Division', lead: 'Staff Coordinator (Pending Migration)', email: 'support@nexoraxdigital.co.uk', tier: 'NexoraX VIP' },
    { id: 'c-2', name: 'NexoraX Digital Ltd', lead: 'Director Direct', email: 'director@nexorax.co.uk', tier: 'Institutional Founder' },
    { id: 'c-3', name: 'Secure Invoice Register Client Resource Workflow Agency Desk', lead: 'Helen / Candidate Office', email: 'Secure Invoice Register-pool@recruiters.com', tier: 'Partner Tier-1' }
  ]);

  const [approvalsQueue, setApprovalsQueue] = useState([
    { id: 'ap-1', requester: 'Search Agent Node-06', type: 'Email Campaign Dispatch', details: 'Cold Outreach Draft targeting 25 takeaways in Dunfermline area.', status: 'PENDING_APPROVAL' },
    { id: 'ap-2', requester: 'Social Media Node-13.3', type: 'Publish Social Media Reel', details: 'UGC Video captioned "Why NexoraX OS Beats Multi-tenant Legacy Services" scheduled.', status: 'PENDING_APPROVAL' }
  ]);

  // Load and synchronize with nexorax_control_plane_tasks in localStorage
  useEffect(() => {
    const saved = localStorage.getItem('nexorax_control_plane_tasks');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          onTasksChanged(parsed);
        } else {
          localStorage.setItem('nexorax_control_plane_tasks', JSON.stringify(tasks));
        }
      } catch (err) {
        console.error("Failed to parse control plane tasks:", err);
      }
    } else if (tasks.length > 0) {
      localStorage.setItem('nexorax_control_plane_tasks', JSON.stringify(tasks));
    }
  }, []);

  const onTasksUpdatedLocal = (updatedArray: WorkSuiteTask[]) => {
    onTasksChanged(updatedArray);
    localStorage.setItem('nexorax_control_plane_tasks', JSON.stringify(updatedArray));
  };

  const handleAddTask = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTaskTitle.trim()) return;

    const task: WorkSuiteTask = {
      id: `task-${Date.now()}`,
      title: newTaskTitle,
      project: newTaskProject,
      client: 'Internal Integration',
      priority: newTaskPriority,
      status: 'TODO'
    };

    const updated = [...tasks, task];
    onTasksUpdatedLocal(updated);
    setNewTaskTitle('');
    onAddLog('Nexus WorkSuite', `Create task: "${newTaskTitle}"`, 'SUCCESS', 'Custom task successfully added to active roster.');
  };

  const handleToggleTask = (id: string) => {
    const updated = tasks.map(t => t.id === id ? {
      ...t,
      status: t.status === 'COMPLETED' ? 'TODO' as const : 'COMPLETED' as const
    } : t);
    onTasksUpdatedLocal(updated);
    const affected = tasks.find(t => t.id === id);
    onAddLog('Nexus WorkSuite', `Toggle task status: "${affected?.title}"`, 'SUCCESS', `Task shifted status.`);
  };

  const handleUpdateTaskStatus = (id: string, newStatus: WorkSuiteTask['status']) => {
    const updated = tasks.map(t => t.id === id ? { ...t, status: newStatus } : t);
    onTasksUpdatedLocal(updated);
    const affected = tasks.find(t => t.id === id);
    onAddLog('Nexus WorkSuite', `Update status of task: "${affected?.title}"`, 'SUCCESS', `Status updated as ${newStatus}.`);
  };

  const handleDeleteTask = (id: string) => {
    const updated = tasks.filter(t => t.id !== id);
    onTasksUpdatedLocal(updated);
    onAddLog('Nexus WorkSuite', 'Deleted task from ledger', 'SUCCESS', 'Removed project action item directly.');
  };

  const handleProcessApproval = (id: string, decision: 'APPROVED' | 'REJECTED') => {
    setApprovalsQueue(prev => prev.filter(item => item.id !== id));
    onAddLog('Nexus WorkSuite', `${decision} Approval: ID ${id}`, decision === 'APPROVED' ? 'SUCCESS' : 'WARNING', `Director action logged on approval matrix.`);
    alert(`Action logged: ${decision}! Automation pipeline has been triggered.`);
  };

  // Completion metrics
  const totalTasksCount = tasks.length;
  const completedTasksCount = tasks.filter(t => t.status === 'COMPLETED' || (t.status as string) === 'DONE').length;
  const inProgressTasksCount = tasks.filter(t => t.status === 'IN_PROGRESS').length;
  const todoTasksCount = tasks.filter(t => t.status === 'TODO').length;
  const progressPct = totalTasksCount > 0 ? Math.round((completedTasksCount / totalTasksCount) * 100) : 0;
  const boundedProgressPct = Math.min(100, Math.max(0, progressPct));

  return (
    <div className="w-full space-y-6">
      {/* Banner + Interactive Progress Bar Grid */}
      <div className="glass-panel border border-[#00ffff]/20 p-6 rounded-2xl bg-gradient-to-r from-purple-950/20 via-black/55 to-cyan-950/20 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
        <div className="md:col-span-7 space-y-2 text-left">
          <h2 className="text-2xl font-sans font-black tracking-tight text-white uppercase flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-cyan-400" />
            Nexus WorkSuite PM
          </h2>
          <p className="text-xs font-mono text-cyan-400/80 uppercase tracking-widest leading-relaxed">
            Enterprise Client Task Roster &amp; Control Plane
          </p>
          <div className="flex flex-wrap gap-2 pt-2 text-[9px] font-mono uppercase">
            <span className="bg-cyan-950/40 border border-cyan-500/15 px-2.5 py-1 rounded text-cyan-400 font-bold">TODO: {todoTasksCount}</span>
            <span className="bg-amber-950/40 border border-amber-500/15 px-2.5 py-1 rounded text-amber-400 font-bold">IN PROGRESS: {inProgressTasksCount}</span>
            <span className="bg-emerald-950/40 border border-emerald-500/15 px-2.5 py-1 rounded text-emerald-400 font-bold">COMPLETED: {completedTasksCount}</span>
          </div>
        </div>

        {/* Dynamic Interactive Progress Bar Card */}
        <div className="md:col-span-5 bg-black/60 border border-slate-900 rounded-xl p-4 space-y-3.5">
          <div className="flex justify-between items-center text-[10px] font-mono">
            <span className="text-cyan-400 font-bold uppercase tracking-wider flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-cyan-400 animate-pulse" />
              TASK COMPLETION INDEX
            </span>
            <span className="text-slate-300 bg-cyan-950/40 border border-cyan-400/15 px-2 py-0.5 rounded text-[11px] font-black">{boundedProgressPct}%</span>
          </div>
          <div className="w-full bg-[#02050c] h-3 border border-slate-800 rounded-full overflow-hidden p-0.5">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${boundedProgressPct}%` }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="h-full bg-gradient-to-r from-cyan-400 via-purple-500 to-emerald-400 shadow-[0_0_15px_rgba(6,182,212,0.65)] rounded-full"
            />
          </div>
          <div className="flex justify-between items-center text-[8.5px] text-slate-500 uppercase tracking-wider">
            <span>Control Plane Status: Nominal</span>
            <span>{completedTasksCount} / {totalTasksCount} milestones achieved</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-zinc-900 gap-4">
        {(['Tasks', 'Projects', 'Clients', 'Approvals'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setActiveTab(t)}
            className={`py-2 px-4 font-mono text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer border-b-2 ${
              activeTab === t
                ? 'border-cyan-400 text-cyan-400 font-bold'
                : 'border-transparent text-slate-500 hover:text-slate-355'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main interactive segment (2/3 col) */}
        <div className="lg:col-span-2">
          {activeTab === 'Tasks' && (
            <div className="space-y-6">
              {/* Task Form */}
              <div className="glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/40">
                <form onSubmit={handleAddTask} className="space-y-4">
                  <h3 className="text-xs font-mono font-black text-white uppercase tracking-wider">
                    Add New Action Item
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="md:col-span-2">
                      <input
                        type="text"
                        placeholder="Task Description..."
                        value={newTaskTitle}
                        onChange={(e) => setNewTaskTitle(e.target.value)}
                        className="w-full bg-black/50 border border-zinc-800 rounded-lg px-3 py-2 text-xs font-mono text-white tracking-wider outline-none focus:border-cyan-400/50 uppercase"
                      />
                    </div>
                    <div>
                      <select
                        value={newTaskPriority}
                        onChange={(e: any) => setNewTaskPriority(e.target.value)}
                        className="w-full bg-[#050510] border border-zinc-800 rounded-lg px-2 py-2 text-xs font-mono text-cyan-400 outline-none"
                      >
                        <option value="URGENT">🔴 URGENT</option>
                        <option value="HIGH">🟡 HIGH</option>
                        <option value="MEDIUM">🟢 MEDIUM</option>
                        <option value="LOW">🔵 LOW</option>
                      </select>
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="w-full py-2.5 bg-cyan-950/80 hover:bg-[#00ffff] hover:text-black text-[#00ffff] border border-cyan-500/20 hover:border-cyan-400 rounded-xl font-mono text-xs font-black uppercase tracking-widest cursor-pointer transition-all"
                  >
                    Add Task to Ledger
                  </button>
                </form>
              </div>

              {/* Task List Header with sorting */}
              <div className="flex justify-between items-center pb-2">
                <span className="text-[10px] font-mono text-slate-500 font-extrabold uppercase">
                  Action Item Kanban Board ({tasks.length})
                </span>
                <NexoraXSortDropdown
                  options={[
                    { value: 'priority', label: 'Node Priority' },
                    { value: 'name', label: 'Name' },
                    { value: 'lastModified', label: 'Last Modified' }
                  ]}
                  selectedValue={sortBy}
                  onSelect={(val) => setSortBy(val as 'priority' | 'name' | 'lastModified')}
                />
              </div>

              {/* Kanban Column drag events helpers */}
              {(() => {
                const handleDragOverCol = (e: React.DragEvent) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                };

                const handleDragEnterCol = (e: React.DragEvent, status: string) => {
                  e.preventDefault();
                  setIsOverColumn(prev => ({ ...prev, [status]: true }));
                };

                const handleDragLeaveCol = (e: React.DragEvent, status: string) => {
                  e.preventDefault();
                  setIsOverColumn(prev => ({ ...prev, [status]: false }));
                };

                const handleDropCol = (e: React.DragEvent, status: 'TODO' | 'IN_PROGRESS' | 'COMPLETED') => {
                  e.preventDefault();
                  setIsOverColumn(prev => ({ ...prev, [status]: false }));
                  const taskId = e.dataTransfer.getData('text/plain');
                  if (taskId) {
                    handleUpdateTaskStatus(taskId, status);
                  }
                };

                return (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {(['TODO', 'IN_PROGRESS', 'COMPLETED'] as const).map((colStatus) => {
                      const colTasks = sortedTasks.filter(item => {
                        const st = item.status?.toUpperCase() || 'TODO';
                        const targetSt = colStatus.toUpperCase();
                        if (targetSt === 'COMPLETED') {
                          return st === 'COMPLETED' || st === 'DONE';
                        }
                        return st === targetSt;
                      });

                      const statusTheme = {
                        TODO: {
                          border: 'border-cyan-500/10 text-cyan-400 bg-cyan-950/5',
                          header: 'TODO',
                          dragOver: 'bg-cyan-950/20 border-cyan-400/60'
                        },
                        IN_PROGRESS: {
                          border: 'border-amber-500/10 text-amber-400 bg-amber-950/5',
                          header: 'IN WORK',
                          dragOver: 'bg-amber-950/20 border-amber-400/60'
                        },
                        COMPLETED: {
                          border: 'border-emerald-500/10 text-emerald-400 bg-emerald-950/5',
                          header: 'COMPLETED',
                          dragOver: 'bg-emerald-950/20 border-emerald-400/60'
                        }
                      }[colStatus];

                      return (
                        <div
                          key={colStatus}
                          onDragOver={handleDragOverCol}
                          onDragEnter={(e) => handleDragEnterCol(e, colStatus)}
                          onDragLeave={(e) => handleDragLeaveCol(e, colStatus)}
                          onDrop={(e) => handleDropCol(e, colStatus)}
                          className={`rounded-xl border p-3 flex flex-col min-h-[350px] transition-all duration-200 ${statusTheme.border} ${
                            isOverColumn[colStatus] 
                              ? `${statusTheme.dragOver} scale-[1.01] shadow-[0_0_15px_rgba(0,255,255,0.08)]` 
                              : 'bg-black/30'
                          }`}
                        >
                          {/* Column Header */}
                          <div className="flex items-center justify-between pb-2 mb-3 border-b border-zinc-900/60 font-mono text-[9px] font-black tracking-widest">
                            <span className="uppercase">{statusTheme.header}</span>
                            <span className="px-1.5 py-0.5 rounded bg-zinc-950 border border-zinc-900 text-white font-bold">{colTasks.length}</span>
                          </div>

                          {/* Tasks Container */}
                          <div className="space-y-2 flex-1 overflow-y-auto max-h-[450px] pr-1 custom-scrollbar">
                            {colTasks.length === 0 ? (
                              <div className="h-full min-h-[100px] flex items-center justify-center border border-dashed border-zinc-900/50 text-[9px] uppercase tracking-wider text-slate-600 rounded-lg py-12 font-bold italic">
                                Drop Items Here
                              </div>
                            ) : (
                              colTasks.map((task) => (
                                <div
                                  key={task.id}
                                  draggable={true}
                                  onDragStart={(e) => {
                                    e.dataTransfer.setData('text/plain', task.id);
                                    e.dataTransfer.effectAllowed = 'move';
                                  }}
                                  className="glass-panel border border-zinc-900 p-3 rounded-lg bg-black/50 hover:bg-black/70 hover:border-zinc-805 transition-all duration-150 cursor-grab active:cursor-grabbing text-left space-y-2 relative group shadow-inner"
                                >
                                  <div>
                                    <div className="flex items-start justify-between gap-1">
                                      <p className={`text-[11px] font-sans font-bold uppercase tracking-tight ${task.status === 'COMPLETED' ? 'line-through text-slate-500' : 'text-white'}`}>
                                        {task.title}
                                      </p>
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteTask(task.id)}
                                        className="opacity-0 group-hover:opacity-100 text-slate-600 hover:text-rose-455 transition-all duration-150 p-1 rounded hover:bg-rose-955/20 cursor-pointer"
                                      >
                                        <Trash2 size={10} />
                                      </button>
                                    </div>
                                    <span className="text-[8px] font-mono text-slate-500 uppercase block mt-0.5">
                                      {task.project}
                                    </span>
                                  </div>

                                  <div className="flex items-center justify-between text-[8px] font-mono pt-1">
                                    <span className={`px-1 rounded font-black border uppercase text-[7px] ${
                                      task.priority === 'URGENT' ? 'bg-rose-950/20 border-rose-500/30 text-rose-400' :
                                      task.priority === 'HIGH' ? 'bg-amber-950/20 border-amber-500/30 text-amber-450' :
                                      task.priority === 'MEDIUM' ? 'bg-emerald-950/20 border-emerald-500/30 text-emerald-400' :
                                      'bg-cyan-950/20 border-cyan-500/30 text-cyan-400'
                                    }`}>
                                      {task.priority}
                                    </span>

                                    <select
                                      value={task.status}
                                      onChange={(e) => handleUpdateTaskStatus(task.id, e.target.value as any)}
                                      className="bg-transparent text-[8px] text-slate-550 hover:text-slate-350 focus:outline-none cursor-pointer font-bold shrink-0 text-right uppercase"
                                    >
                                      <option value="TODO" className="bg-slate-950 text-cyan-400">TODO</option>
                                      <option value="IN_PROGRESS" className="bg-slate-950 text-amber-400">IN WORK</option>
                                      <option value="COMPLETED" className="bg-slate-950 text-emerald-400">DONE</option>
                                    </select>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}

          {activeTab === 'Projects' && (
            <div className="space-y-4">
              {projects.map((proj) => (
                <div key={proj.id} className="glass-panel border border-zinc-900 p-5 rounded-2xl bg-black/30 space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="text-xs font-sans font-black text-white uppercase tracking-tight">{proj.name}</h4>
                      <span className="text-[9px] font-mono text-slate-500 uppercase">Client: {proj.client}</span>
                    </div>
                    <span className="px-2 py-0.5 rounded bg-cyan-950/40 border border-cyan-500/20 text-[#00ffff] font-mono text-[8px] uppercase">
                      {proj.status}
                    </span>
                  </div>

                  <div className="space-y-1">
                    <div className="flex justify-between text-[9px] font-mono text-slate-500">
                      <span>INTEGRATION MILESTONES COMPLETE:</span>
                      <span className="text-white font-bold">{proj.pct}%</span>
                    </div>
                    <div className="w-full bg-zinc-950 h-1.5 rounded-full">
                      <div className="bg-gradient-to-r from-cyan-400 to-purple-600 h-full rounded-full" style={{ width: `${proj.pct}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'Clients' && (
            <div className="space-y-4">
              {clients.map((cli) => (
                <div key={cli.id} className="glass-panel border border-zinc-900 p-5 rounded-2xl bg-black/30 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="space-y-1">
                    <h4 className="text-xs font-sans font-black text-white uppercase tracking-tight">{cli.name}</h4>
                    <span className="text-[9px] font-mono text-slate-400 block">Lead Coordinator: {cli.lead}</span>
                    <span className="text-[9px] font-mono text-slate-500 lowercase block">{cli.email}</span>
                  </div>
                  <span className="self-start md:self-auto px-3 py-1 bg-purple-950/40 border border-purple-500/20 text-purple-300 rounded-lg text-[9px] font-mono font-black uppercase tracking-wider">
                    {cli.tier}
                  </span>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'Approvals' && (
            <div className="space-y-4">
              {approvalsQueue.length === 0 ? (
                <div className="text-center py-12 bg-black/20 border border-zinc-900 rounded-2xl text-xs font-mono text-slate-500 uppercase">
                  All automation outputs approved. Queue clear.
                </div>
              ) : (
                approvalsQueue.map((item) => (
                  <div key={item.id} className="glass-panel border border-purple-500/20 p-5 rounded-2xl bg-gradient-to-r from-purple-950/10 to-black/25 space-y-4">
                    <div className="flex justify-between border-b border-zinc-900 pb-2">
                      <span className="text-[10px] font-mono text-[#00ffff] font-black uppercase tracking-wider">
                        {item.type}
                      </span>
                      <span className="px-2 py-0.5 rounded bg-amber-950/30 border border-amber-500/20 text-amber-500 font-mono text-[8px] uppercase font-black">
                        {item.status}
                      </span>
                    </div>

                    <div className="space-y-1 text-xs font-sans">
                      <p className="text-slate-300 leading-normal normal-case font-medium">
                        {item.details}
                      </p>
                      <p className="text-[9px] font-mono text-slate-500 uppercase pt-1">
                        Trigger Source: <strong className="text-slate-400">{item.requester}</strong>
                      </p>
                    </div>

                    {/* Decision Button Panel */}
                    <div className="grid grid-cols-2 gap-2 text-[9px] font-mono font-black uppercase">
                      <button
                        onClick={() => handleProcessApproval(item.id, 'APPROVED')}
                        className="py-1.5 bg-[#00ffff] hover:opacity-90 text-black rounded cursor-pointer"
                      >
                        ✔ Grant Approval
                      </button>
                      <button
                        onClick={() => handleProcessApproval(item.id, 'REJECTED')}
                        className="py-1.5 bg-rose-950/40 hover:bg-rose-950 text-rose-400 border border-rose-500/20 rounded cursor-pointer"
                      >
                        ✖ Reject & Hold
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Right Automation Pipeline / Connections (1/3 col) */}
        <div className="space-y-6">
          {/* Active Integration Automations */}
          <div className="glass-panel border border-zinc-800 p-5 rounded-2xl bg-black/30 space-y-4 text-xs font-mono">
            <h3 className="text-xs font-sans font-black text-white uppercase tracking-widest border-b border-zinc-900 pb-2 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#00ffff]" />
              Worksuite Automations
            </h3>

            <div className="space-y-3 text-[10px]">
              <div className="p-3 bg-black/50 border border-zinc-900 rounded-xl space-y-1 shadow-inner">
                <div className="text-[#00ffff] uppercase font-black">Search Item to Task</div>
                <p className="text-[9px] text-slate-400 leading-normal normal-case">
                  Sending search index metadata directly generates task priorities within client buckets automatically.
                </p>
              </div>

              <div className="p-3 bg-black/50 border border-zinc-900 rounded-xl space-y-1 shadow-inner">
                <div className="text-purple-400 uppercase font-black">Customer Query to Ticket</div>
                <p className="text-[9px] text-slate-400 leading-normal normal-case">
                  Inbound chat transcripts with unresolved questions pipeline straight into administrative teams rosters.
                </p>
              </div>

              <div className="p-3 bg-black/50 border border-zinc-900 rounded-xl space-y-1 shadow-inner">
                <div className="text-[#00ffff] uppercase font-black">Meeting Notes Summarizer</div>
                <p className="text-[9px] text-slate-400 leading-normal normal-case">
                  Combines speaker transcripts and produces step-by-step action sheets with pre-assigned AI sub-agents.
                </p>
              </div>
            </div>
          </div>

          {/* CRM Lite Status Widget */}
          <div className="glass-panel border border-zinc-800 p-5 rounded-2xl bg-black/30 text-xs font-mono space-y-3">
            <h3 className="text-xs font-sans font-black text-white uppercase tracking-widest border-b border-zinc-900 pb-2 flex items-center gap-2">
              <Briefcase className="w-3.5 h-3.5 text-cyan-400" />
              CRM Pipeline Summary
            </h3>
            <div className="space-y-2 text-[10px]">
              <div className="flex justify-between">
                <span className="text-slate-500">TOTAL LEADS:</span>
                <span className="text-white font-extrabold">24 active targets</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">POTENTIAL YIELD:</span>
                <span className="text-emerald-400 font-extrabold">£194,500</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">MIGRATION TARGET:</span>
                <span className="text-purple-400 font-extrabold">Staff Coordinator (Pending Compliance)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
